import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import { ACTION_SYNC_MODES } from "../../../../../constants/Constants";

/**
 * The "Sync actions" section of the Synchronize menus — which viewports a
 * window-level, zoom or pan reaches.
 *
 * Shared rather than repeated because the same menu is built in three places
 * (the desktop toolbar tool, the portrait More flyout and the mobile bottom
 * menu) and a scope that behaved differently depending on which one you opened
 * would be a bug nobody could reproduce.
 *
 * A hanging protocol's `syncActions` sets the same state, so a protocol and this
 * menu are two ways of driving one setting — whichever spoke last wins, which is
 * how the scroll-sync mode above it already behaves.
 */
const ActionSyncMenuItems = ({ onSelect, rowPadding = "0 12px" }) => {
  const { t } = useTranslation();
  const { actionSyncMode, setActionSyncMode } = useContext(ViewportContext);

  // Built here rather than at module level, as the sync-mode lists in the menus
  // that render this are: Constants re-exports the Viewer, so the toolbar import
  // chain re-enters Constants while it is still initialising. Reading
  // ACTION_SYNC_MODES at module-evaluation time hits its temporal dead zone.
  const actionSyncItems = [
    {
      mode: ACTION_SYNC_MODES.DISABLED,
      labelKey: "viewer.tools.action-sync-disabled",
      fallback: "Disabled",
    },
    {
      mode: ACTION_SYNC_MODES.ALL,
      labelKey: "viewer.tools.action-sync-all",
      fallback: "All viewports",
    },
    {
      mode: ACTION_SYNC_MODES.SERIES,
      labelKey: "viewer.tools.action-sync-series",
      fallback: "Same series",
    },
    {
      mode: ACTION_SYNC_MODES.LINKED,
      labelKey: "viewer.tools.action-sync-linked",
      fallback: "Linked series",
    },
  ];

  /**
   * Only the state is set. The Viewport effect owns registration, because it is
   * the one place that knows when registering is safe — mid-hang, a protocol is
   * still writing its own per-viewport windowing and alignment, and a live
   * synchronizer would spread the first of those writes over all the others.
   */
  const handleActionSyncChange = (mode) => {
    setActionSyncMode(mode);
    onSelect?.();
  };

  return (
    <>
      <div className="viewer-sync-menu-divider" />
      <div className="viewer-sync-menu-section-label">
        {t("viewer.tools.action-sync", "Sync actions")}
      </div>
      {actionSyncItems.map(({ mode, labelKey, fallback }) => {
        const isSelected = (actionSyncMode ?? ACTION_SYNC_MODES.DISABLED) === mode;
        return (
          <div
            key={mode}
            className="stack-scroll-dropdown-elements"
            onClick={() => handleActionSyncChange(mode)}
            style={{
              justifyContent: "space-between",
              gap: "10px",
              cursor: "pointer",
              padding: rowPadding,
            }}
          >
            <span>{t(labelKey, fallback)}</span>
            <span
              style={{
                display: "inline-block",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: isSelected ? "#0078d4" : "#585858",
                border: isSelected ? "none" : "1px solid #888",
                flexShrink: 0,
              }}
            />
          </div>
        );
      })}
    </>
  );
};

export default ActionSyncMenuItems;
